import {
  BillingAddressDto,
  PaymentMethodDetailsWithAddressDto,
  PdfRemediationBillingCycle,
  SiteSearchEntryDto,
  SubscriptionBillingCycleType,
  UserwaySubjectTransactionType,
} from '../../userway/sdk/UserWayApiClient';
import {PurchaseProductOrAddonProperties} from '../services/ampli';

export type TierPlan = PdfPlanTier;

export type BillingCycleType = SubscriptionBillingCycleType | PdfRemediationBillingCycle;

export enum PdfPlanTier {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CUSTOM = 'CUSTOM',
}

export const PDFS_NUMBER_FOR_CUSTOM_PLAN = 501;

export enum ProductType {
  WhiteLabel = 'WHITE_LABEL',
  CustomBranding = 'CUSTOM_BRANDING',
  ModifyMenu = 'MODIFY_MENU',
  UsageStats = 'USAGE_STATS',
  LiveTranslations = 'LIVE_TRANSLATIONS',
  Remediation = 'REMEDIATION',
  Remediation_Plus = 'REMEDIATION_PLUS',
  Remediation_Ultimate = 'REMEDIATION_ULTIMATE',
  ScanningAndMonitoring = 'SCANNING_AND_MONITORING',
  ImgRemediation = 'IMG_REMEDIATION',
  PdfRemediation = 'PDF_REMEDIATION',
  PdfAutoRemediation = 'PDF_AUTO_REMEDIATION',
}

export const NO_TRIAL_PRODUCTS: ProductType[] = [
  ProductType.LiveTranslations,
  ProductType.ScanningAndMonitoring,
  ProductType.ImgRemediation,
  ProductType.PdfRemediation,
  ProductType.PdfAutoRemediation,
];

export interface StripeProduct {
  id: string;
  name: ProductType;
  checked: boolean;
  mandatory: boolean;
  title?: string;
  description?: string;
  tiers: Tier[];
}

export type Tier = {
  baseCostBiAnnual: number;
  baseCostBiAnnualDiscounted: number;
  baseCostMonthly: number;
  baseCostMonthlyDiscounted: number;
  baseCostYearly: number;
  baseCostYearlyDiscounted: number;
  baseDiscountPercentBiAnnual: number;
  baseDiscountPercentYearly: number;
  id: TierPlan;
  quota?: number;
};

export interface ICalculateRequest {
  address?: BillingAddress;
  billingCycle?: BillingCycleType;
  coupon?: string;
  customerId: string;
  subscriptionId?: string;
  items: ICalculateItemRequest[];
}

export interface ICalculateSubUpgradeRequest {
  formOrigin?: string;
  customerId: string;
  subscriptionId: string;
  coupon?: string;
  address?: BillingAddress;
}

interface ICalculateItemRequest {
  productId: string;
  sitesIds: number[];
  settings?: {
    pdfRemediationOrderId?: number;
    pricing_tier?: TierPlan;
    flowId?: string;
    quantity?: number;
  };
}

export interface CalculateActivePayload {
  couponApplied: boolean;
  estimate: {
    REMEDIATION?: ICalculateEstimateItem[];
    WHITE_LABEL?: ICalculateEstimateItem[];
    CUSTOM_BRANDING?: ICalculateEstimateItem[];
    MODIFY_MENU?: ICalculateEstimateItem[];
    USAGE_STATS?: ICalculateEstimateItem[];
    LIVE_TRANSLATIONS?: ICalculateEstimateItem[];
    PDF_REMEDIATION: ICalculateEstimateItem[];
    PDF_AUTO_REMEDIATION: ICalculateEstimateItem[];
  };
  invoice: any;
  invoiceDto: InvoiceTotal;
}

export interface ICalculateEstimateItem {
  cost: number;
  discount: number;
  discountPercents: number;
  siteItem: ICalculateEstimateItemSite;
}

export interface ICalculateAutomatedPdfPlan {
  cost: number;
  discount: number;
  discountPercents: number;
}

export interface ICalculateEstimateItemSite {
  host: string;
  id: number;
  url: string;
  views: number;
}

export interface InvoiceTotal {
  discountUsd: number;
  subtotalUsd: number;
  taxRate: number;
  taxUsd: number;
  totalUsd: number;
  discountPercentage: number;
}

export enum BillingCycleName {
  MONTHLY = 'Monthly',
  BIMONTHLY = 'Bimonthly',
  ANNUAL = 'Annual',
  BIANNUAL = '2-Year',
  QUARTERLY = 'Quarterly',
  CUSTOM = 'Custom',
}

export enum PaymentStep {
  SELECT_PLAN = 'SELECT_PLAN',
  PAYMENT_FORM = 'PAYMENT_FORM',
}

export enum SubscriptionMonthAmount {
  MONTH = 1,
  BIMONTH = 2,
  QUARTER = 3,
  ANNUAL = 12,
  BIANNUAL = 24,
}

export interface BillingAddress {
  line1: string;
  state: string;
  city: string;
  country: string;
  postalCode: string;
}

export interface IBillingDetails {
  name: string;
  email: string;
  address: {
    city: string;
    country: string;
    line1: string;
    postal_code: string;
    state?: string;
  };
}

export enum IPaymentIntentStatus {
  requiresPaymentMethod = 'requires_payment_method',
  requiresAction = 'requires_action',
  succeeded = 'succeeded',
}

export interface IStripePaymentMethodDefaultPayload {
  payload: PaymentMethodDetailsWithAddressDto;
  payloadType: string;
}

export interface IStripePaymentMethodDefaultAddress {
  country: string;
  state?: string;
  postalCode: string;
  city: string;
  line1: string;
}

export interface IStripeMultiPaymentMethods {
  uwPaymentDataId: number;
  providerPaymentMethodId: string;
  provider: string;
  method: string;
  isDefault: boolean;
  alias: string | null;
  card: IStripeMultiPaymentMethodsCard;
}

export interface IStripeMultiPaymentMethodsCard {
  type: string;
  expYear: number;
  expMonth: number;
  lastFour: string;
  firstName: string;
  lastName: string;
}

export interface IStripeCard {
  last4: string;
  exp_month: number;
  exp_year: number;
  brand: string;
}

export interface ICartItem {
  productId: string;
  sitesIds: number[];

  settings?: {
    pricing_tier?: string;
    liveTranslationPricingTier?: string;
    pdfRemediationOrderId?: number;
    scans?: number;
    flowId?: string;
    quantity?: number;
  };
}

export interface ICalculateStripeCartResponse {
  couponApplied: boolean;
  estimate: any;
  invoice: IStripeInvoice;
  invoiceDto: IStripeInvoiceDto;
}

export interface IStripeInvoice {
  id: string;
  total: number;
  subtotal: number;
  tax: number;
  currency: string;
  payment_intent?: IStripePaymentIntent;
  discount?: any;
}

export interface IStripeInvoiceDto {
  discountUsd: number;
  subtotalUsd: number;
  taxRate: number;
  taxUsd: number;
  totalUsd: number;
  discountPercentage: number;
}

export interface IConfirmStripeInvoiceRequest {
  subscriptionId: string;
  formOrigin: string;
  paymentMethodId: string;
}

export interface INewSubscriptionData {
  customerId: string;
  paymentMethodId: string;
  items: ICartItem[];
  billingCycle: BillingCycleType;
  formOrigin: string;
  trial: boolean;
  coupon: string;
  subscriptionId?: string;
  cancelRunningSubscription: boolean | null;
}

export interface IPaymentMethodRequest {
  paymentMethodId: string;
}

export interface IRetryStripeInvoiceRequest {
  customerId?: string;
  paymentMethodId?: string;
  invoiceId?: string;
  formOrigin?: string;
  subscriptionId?: string;
}

export interface IStripePaymentIntent {
  status: IPaymentIntentStatus;
  client_secret: string;
  payment_method?: string;
  amount: number;
  charges: {
    data: [IStripeCharge];
  };
}

export interface IStripeCharge {
  id: string;
  billing_details: IBillingDetails;
  payment_method_details: {
    card: IStripeCard;
    type: string;
  };
}

export interface IStripeSubscriptionResponse {
  subscription: IStripeSubscription;
  user?: IStripeUser;
}

export interface IStripeSubscription {
  id: string;
  status: string;
  latest_invoice?: IStripeInvoice;
  error?: any;
}

export interface IStripeUser {
  aiPaidUser: boolean;
  totalSitesLoads: number;
}

export interface ISwitchToAnnual {
  customerId: string;
  subscriptionId: string;
}

export interface InvoiceBody {
  formOrigin: string;
  customerId: string;
  paymentMethodId: string;
  items: InvoiceBodyItem[];
}

interface InvoiceBodyItem {
  productId: string;
  sitesIds: string[];
  settings: {
    pdfRemediationOrderId: string;
  };
}

export interface CalculateOnTrialActive {
  formOrigin: string;
  customerId: string;
  subscriptionId: string;
  coupon?: string;
  address?: BillingAddress;
}

export interface CalculateOnTrialActivePayload {
  couponApplied: boolean;
  invoice: any;
  invoiceDto: InvoiceTotal;
}

export interface CalculateOnTrialActiveResponse {
  payload: CalculateOnTrialActivePayload;
  payloadType: string;
}

export interface CalculateActiveResponse {
  payload: CalculateActivePayload;
  payloadType: string;
}

export interface IPaymentMethodDetails {
  paymentMethodId?: any;
  stripeInstance: stripe.Stripe;
  card?: stripe.elements.Element;
}

export interface IPaymentStatus {
  isPaymentRetry?: boolean;
  isPaymentOnTrial?: boolean;
  isNoCardTrial?: boolean;
}

export interface IPaymentIdentifiers {
  invoiceId?: string;
  subscriptionId?: string;
  subscriptionIdNumber?: number;
  customerId: string;
  userServiceId?: number;
  orderId?: number;
  flowId?: string;
  splIdentityHash?: string;
  quoteHash?: string;
}

export interface IProductDetails {
  sitesIds?: number[];
  productIds?: string[];
  selectedProduct?: ProductType;
  items?: ICartItem[];
  quantity?: number;
  tiers?: TierPlan;
  liveTranslationsPlan?: TierPlan;
}

export interface IBillingInformation {
  billingDetails?: IBillingDetails;
  billingCycle: BillingCycleType;
}

export interface IPaymentSettings {
  origin: string;
  trial: boolean;
  coupon: string;
  cancelRunningSubscription?: boolean | null;
}

export interface IPaymentCallbacks {
  setLatestInvoice: (latestInvoicePaymentIntentStatus: IPaymentIntentStatus, latestInvoiceId: string) => void;
  onError: (event: stripe.elements.ElementChangeResponse | stripe.PaymentMethodResponse | stripe.Error) => void;
  onSuccess: (response?: IStripeSubscriptionResponse) => void;
}

export interface PaymentData
  extends IPaymentMethodDetails,
    IPaymentStatus,
    IPaymentIdentifiers,
    IProductDetails,
    IBillingInformation,
    IPaymentSettings,
    IPaymentCallbacks {}

export type Country = {
  name: string;
  code: string;
  states?: {
    name: string;
    code: string;
  }[];
  zipCodeRegExp?: RegExp;
};

export enum ChangePlanType {
  SwitchToAnnual,
  Trial,
}

export type Plan = {
  title: string;
  icon: string;
  value: string;
  desc: string;
  price: number;
  upTo: string;
  id: ProductType;
  btnTitle: string;
  benefits: string[];
  loading: boolean;
  purchasedProperties?: PurchaseProductOrAddonProperties;
};

export interface UserSharedData {
  siteIds: number[];
  productIds: string[];
  billingCycle: SubscriptionBillingCycleType;
}

export interface UserSharedPayload {
  payload: string;
  payloadType: string;
}

export enum ProductNames {
  WHITE_LABEL = 'WHITE_LABEL',
  CUSTOM_BRANDING = 'CUSTOM_BRANDING',
  MODIFY_MENU = 'MODIFY_MENU',
  USAGE_STATS = 'USAGE_STATS',
  LIVE_TRANSLATIONS = 'LIVE_TRANSLATIONS',
  REMEDIATION = 'REMEDIATION',
  REMEDIATION_PLUS = 'REMEDIATION_PLUS',
  SCANNING_AND_MONITORING = 'SCANNING_AND_MONITORING',
  PDF_AUTO_REMEDIATION = 'PDF_AUTO_REMEDIATION',
}

export interface SharedPaymentLinkData {
  user_id: number;
  type: SharedPaymentLinkType;
  payload?: SharedPaymentLinkPayload;
  quoteModel?: any;
}

export interface SharedPaymentLinkPayload {
  forceCalculation?: boolean;
  mandatoryProductIds?: string[];
  productIds?: string[];
  subscriptionId?: number;
  sites?: BillingSiteOrderItem[];
  siteIds?: number[];
  billingCycle?: SubscriptionBillingCycleType;
  createdByAdmin?: boolean;
  isIsraeliTx?: boolean;
  trialDays?: number;
  trial?: boolean;
  scannerProduct?: any;
  total?: number;
  providerSubscriptionId?: string;
  subscription?: any;
  triaDays?: number;
  hash?: string; // quoteHash
}

export interface BillingSiteOrderItem {
  id: number;
  host: string;
  url: string;
  views: number;
}

export interface GrantedUserSharedPaymentLink {
  id: number;
  code: string;
  email: string;
  first_name: string;
  last_name: string;
  discount: string;
  discount_expires_at: string;
  aiTrialApplicable: boolean;
}

export interface SharedPaymentLinkResponse {
  sharedPaymentLink: SharedPaymentLinkData;
  user: GrantedUserSharedPaymentLink;
}

export enum SharedPaymentLinkType {
  COMPLETE_YOUR_PAYMENT = 'COMPLETE_YOUR_PAYMENT',
  CALL_TO_ACTION = 'CALL_TO_ACTION',
  CYP_ADMIN = 'CYP_ADMIN',
  CYP_ADMIN_GENERAL = 'CYP_ADMIN_GENERAL',
  QUOTE = 'QUOTE',
  USER_SHARED = 'USER_SHARED',
  UPGRADE_TO_PRO_PLUS = 'UPGRADE_TO_PRO_PLUS',
}

export interface BillingHelperProductTieredItemDto {
  name: ProductNames;
  id: string;
  tiers: BillingHelperItemCostDto[];
  checked?: boolean;
}

export interface BillingHelperItemCostDto {
  id?: string;
  quota?: number;
  baseCostMonthly?: number;
  baseCostYearly?: number;
  baseCostBiAnnual?: number;
  baseCostMonthlyDiscounted?: number;
  baseCostYearlyDiscounted?: number;
  baseCostBiAnnualDiscounted?: number;
  oneTimeCost?: number;
  oneTimeCostDiscounted?: number;
  checked?: boolean;
  mandatory?: boolean;
  baseDiscountPercentBiAnnual: number;
  baseDiscountPercentYearly: number;
}

export enum ScanningAndMonitoringTier {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CUSTOM = 'CUSTOM',
}

export interface MySite extends SiteSearchEntryDto {
  views?: number;
  preselected?: boolean;
}

export interface MySitesSearchResponse {
  sites: MySite[];
  total: number;
  moreAvailable: boolean;
  pageIdx: number;
  perPage: number;
}

export interface QuoteProductItem {
  type: QuoteProductItemType;
  site?: QuoteProductItemSite;
  products: QuoteProductItemEntry[];
}

export interface QuoteProductItemSite {
  host: string;
  url?: string;
  views: number;
  tier: string;
  tierViews: number;
  customFields?: Record<string, unknown>;
  iconUrl: string;
}

export interface QuoteProductItemEntry {
  productId: UserwaySubjectTransactionType;
  title: string;
  tier: string;
  price: number;
  pages?: number;
  comment?: string;
  tierId?: string;
  key?: string;
  isNotIncluded?: boolean;
  customFields?: Record<string, unknown>;
}

export const enum QuoteProductItemType {
  RECURRING = 'RECURRING',
  NON_RECURRING = 'NON_RECURRING',
  BULK = 'BULK',
}

export interface QuoteModel {
  recurringSiteProducts: QuoteProductItem[];
  recurringProducts: QuoteProductItem[];
  nonRecurringProducts: QuoteOverviewItem[];
  pricingInfo: QuotePricingInfo;
}

export interface QuoteOverviewItem {
  type: QuoteProductItemType;
  title: string;
  tier: string;
  price: number;
  quantity: number;
  isNotIncluded?: boolean;
  discount?: QuoteDiscount;
  comment?: string;
}

export const enum QuoteDiscountType {
  PERCENT = 'PERCENT',
  EXACT = 'EXACT',
}

export interface QuoteDiscount {
  type: QuoteDiscountType;
  value: number;
  isNonProfitable?: boolean;
}

export interface QuotePricingInfo {
  subtotal: number;
  total: number;
  allowAch?: boolean;
  hideTotals?: boolean;
  hideYearDiscountComments?: boolean;
  effectiveDate?: number;
  effectiveDateText?: string;
  initialTerm?: number;
  initialTermText?: string;
  discount?: QuoteDiscount;
  price2Year: number | null;
  price3Year: number | null;
  price5Year: number | null;
  taxesOneYear?: number | null;
  taxesTwoYears?: number;
  taxesThreeYears?: number;
  taxesFiveYears?: number;
  intaxableTotal?: number;
  intaxablePrice2Year: number;
  intaxablePrice3Year: number;
  intaxablePrice5Year: number;
}

export enum OriginPaymentType {
  MANAGE_CYP = 'MANAGE_CYP', // Complete your payment link
  MANAGE_CYP_F = 'MANAGE_CYP_F', // Complete your payment link
  MANAGE_CTA = 'MANAGE_CTA', // Call to action
  MANAGE_AM = 'MANAGE_AM', // Agency mode
  WORDPRESS_PLATFORM = 'WORDPRESS_PLATFORM',
  BIGCOMMERCE_PLATFORM = 'BIGCOMMERCE_PLATFORM',
  DRUPAL_PLATFORM = 'DRUPAL_PLATFORM',
  JOOMLA_PLATFORM = 'JOOMLA_PLATFORM',
  MAGENTO_PLATFORM = 'MAGENTO_PLATFORM',
  SQUAREUP_PLATFORM = 'SQUAREUP_PLATFORM',
}
